export function createVoiceScript() {
    let line = null;
    let nextId = 1;
    const listeners = new Set();
    const emit = () => listeners.forEach((l) => l());
    return {
        getSnapshot: () => line,
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        start(text, durationMs = null) {
            const clean = text.trim();
            if (!clean)
                return;
            const ms = durationMs !== null && Number.isFinite(durationMs) && durationMs > 0 ? durationMs : null;
            line = { id: nextId++, text: clean, durationMs: ms };
            emit();
        },
        stop() {
            if (!line)
                return;
            line = null;
            emit();
        },
    };
}
/** One script per running client, next to voicePerformance. */
export const voiceScript = createVoiceScript();
const CYRILLIC = {
    а: 'a',
    б: 'b',
    в: 'v',
    г: 'g',
    д: 'd',
    ђ: 'đ',
    е: 'e',
    ж: 'ž',
    з: 'z',
    и: 'i',
    ј: 'j',
    к: 'k',
    л: 'l',
    љ: 'lj',
    м: 'm',
    н: 'n',
    њ: 'nj',
    о: 'o',
    п: 'p',
    р: 'r',
    с: 's',
    т: 't',
    ћ: 'ć',
    у: 'u',
    ф: 'f',
    х: 'h',
    ц: 'c',
    ч: 'č',
    џ: 'dž',
    ш: 'š',
};
const DIGRAPHS = {
    dž: 'CH',
    lj: 'nn',
    nj: 'nn',
    th: 'TH',
    sh: 'CH',
    ch: 'CH',
};
const LETTERS = {
    a: 'aa',
    e: 'E',
    i: 'I',
    o: 'O',
    u: 'U',
    y: 'I',
    w: 'U',
    p: 'PP',
    b: 'PP',
    m: 'PP',
    f: 'FF',
    v: 'FF',
    t: 'DD',
    d: 'DD',
    n: 'nn',
    l: 'nn',
    k: 'kk',
    g: 'kk',
    h: 'kk',
    q: 'kk',
    x: 'kk',
    s: 'SS',
    z: 'SS',
    c: 'SS',
    š: 'CH',
    ž: 'CH',
    č: 'CH',
    ć: 'CH',
    đ: 'CH',
    r: 'RR',
    j: 'I',
};
const VOWELS = new Set(['aa', 'E', 'I', 'O', 'U']);
const toLatin = (word) => [...word.toLowerCase()].map((ch) => CYRILLIC[ch] ?? ch).join('');
/**
 * Serbian is phonetic (one letter, one sound), so visemes come straight from the letters.
 * Returns [viseme, relative duration weight] pairs.
 */
export function wordVisemes(word) {
    const w = toLatin(word);
    const out = [];
    for (let i = 0; i < w.length; i++) {
        let v = DIGRAPHS[w.slice(i, i + 2)];
        if (v)
            i++;
        else
            v = LETTERS[w[i] ?? ''];
        if (!v)
            continue;
        if (out.length && out[out.length - 1]?.[0] === v)
            continue;
        out.push([v, VOWELS.has(v) ? 1 : 0.7]);
    }
    return out;
}
/**
 * Word and viseme timings for a line. Without a real duration the timing is estimated from the
 * text; with one, the estimate is stretched to fit the actual audio.
 */
export function lipSync(text, durationMs = null) {
    const words = text.split(/\s+/).filter(Boolean);
    const wtimes = [];
    const wdurations = [];
    let t = 120;
    for (const w of words) {
        const letters = toLatin(w).replace(/[^\p{L}\p{N}]/gu, '').length;
        const d = Math.max(130, letters * 68);
        wtimes.push(t);
        wdurations.push(d);
        t += d + (/[.!?…]$/.test(w) ? 360 : /[,;:]$/.test(w) ? 170 : 55);
    }
    let total = t + 150;
    if (durationMs) {
        const k = durationMs / total;
        for (let i = 0; i < words.length; i++) {
            wtimes[i] = (wtimes[i] ?? 0) * k;
            wdurations[i] = (wdurations[i] ?? 0) * k;
        }
        total = durationMs;
    }
    const visemes = [];
    const vtimes = [];
    const vdurations = [];
    words.forEach((word, i) => {
        const list = wordVisemes(word);
        const sum = list.reduce((s, [, weight]) => s + weight, 0);
        if (!sum)
            return;
        let at = wtimes[i] ?? 0;
        for (const [v, weight] of list) {
            const d = (weight / sum) * (wdurations[i] ?? 0);
            visemes.push(v);
            vtimes.push(at);
            vdurations.push(d);
            at += d;
        }
    });
    return { words, wtimes, wdurations, visemes, vtimes, vdurations, total };
}
/** Whole words/phrases (letters on either side don't count), Unicode-aware for š, ć, ž. */
const words = (...list) => new RegExp(`(?<!\\p{L})(?:${list.join('|')})(?!\\p{L})`, 'u');
const CUES = [
    [/😢|😔/u, { mood: 'sad', gesture: null, emoji: '😔' }],
    [
        words('nažalost', 'izvini', 'žao mi je', 'tužn\\p{L}*', 'sorry', 'unfortunately', 'leider'),
        { mood: 'sad', gesture: null, emoji: '😔' },
    ],
    [/😍|🥰|❤/u, { mood: 'love', gesture: null, emoji: '🥰' }],
    [words('volim te', 'obožavam', 'dušo', 'love you'), { mood: 'love', gesture: null, emoji: '🥰' }],
    [/😮|😳/u, { mood: 'fear', gesture: null, emoji: '😳' }],
    [
        words('ups', 'opa', 'joj', 'wow', 'stvarno\\?', 'ozbiljno\\?', 'oh no'),
        { mood: 'fear', gesture: null, emoji: '😳' },
    ],
    [
        words('ne smem', 'zabranjeno', 'nije dozvoljeno', 'odbijam'),
        { mood: 'angry', gesture: 'thumbdown', emoji: null },
    ],
    [
        words('ćao', 'zdravo', 'hej', 'dobro jutro', 'dobar dan', 'dobro veče', 'hello', 'hi', 'hallo'),
        { mood: 'happy', gesture: 'handup', emoji: '😊' },
    ],
    [/😂|🤣|haha|hehe/u, { mood: 'happy', gesture: null, emoji: '😂' }],
    [
        words('gotovo', 'urađeno', 'uključ\\p{L}*', 'isključ\\p{L}*', 'upali\\p{L}*', 'ugasi\\p{L}*', 'pustila', 'poslala', 'zakazala', 'zapisala', 'sačuvala', 'done'),
        { mood: 'happy', gesture: 'ok', emoji: null },
    ],
    [
        words('super', 'odlično', 'bravo', 'sjajno', 'naravno', 'važi', 'može', 'great', 'perfect'),
        { mood: 'happy', gesture: 'thumbup', emoji: '😊' },
    ],
    [
        words('ne znam', 'nisam sigurna', 'možda', 'hmm', 'teško je reći', 'not sure'),
        { mood: 'neutral', gesture: 'shrug', emoji: '🤔' },
    ],
    [
        words('prvo', 'zapamti', 'važno', 'pazi', 'obrati pažnju'),
        { mood: 'neutral', gesture: 'index', emoji: null },
    ],
    [/!|😊|🙂/u, { mood: 'happy', gesture: null, emoji: null }],
];
/**
 * Picks a mood and a gesture from what Elena says. Cheap keyword cues, Serbian first, with
 * English/German basics; neutral and no gesture when nothing stands out.
 */
export function actingFor(text) {
    const t = toLatin(text);
    for (const [cue, acting] of CUES)
        if (cue.test(t))
            return acting;
    return { mood: 'neutral', gesture: null, emoji: null };
}
//# sourceMappingURL=voice-script.js.map